import { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import 'react-quill/dist/quill.snow.css';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { sheetPages, type SheetContent } from './types';

/** Renders a sheet's content1..5 with a Previous / Next pager (same as old_obd). */
export function SheetPages({ sheet }: { sheet: SheetContent }) {
  const pages = useMemo(() => sheetPages(sheet), [sheet]);
  const [index, setIndex] = useState(0);

  useEffect(() => setIndex(0), [sheet.id]);

  const safeIndex = Math.min(index, Math.max(pages.length - 1, 0));
  // Content is HTML written by an admin: always sanitize before rendering.
  const html = useMemo(() => DOMPurify.sanitize(pages[safeIndex] ?? ''), [pages, safeIndex]);

  if (pages.length === 0) {
    return <p className="text-sm text-muted-foreground">No content available yet.</p>;
  }

  return (
    <div className="grid gap-3">
      <div className="ql-snow rounded-lg border border-border bg-card">
        <div className="ql-editor" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
      {pages.length > 1 && (
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={safeIndex === 0} onClick={() => setIndex(safeIndex - 1)}>
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Sheet {safeIndex + 1} of {pages.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={safeIndex >= pages.length - 1}
            onClick={() => setIndex(safeIndex + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
